import React, { createContext, useContext, useState, useEffect } from 'react';
// aria-label: design-system-bypass 


export interface CartItem {
    id: string;
    name: string;
    price: number;
    qty: number;
    image: string;
    type?: 'equipment' | 'carbon';
}

interface CartContextType {
    cartItems: CartItem[];
    addToCart: (item: CartItem) => void;
    removeFromCart: (id: string) => void;
    updateQty: (id: string, qty: number) => void;
    clearCart: () => void;
    buyNow: (item: CartItem) => void;
    cartTotal: number;
    cartCount: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [cartItems, setCartItems] = useState<CartItem[]>(() => {
        const saved = localStorage.getItem('annadata_cart');
        return saved ? JSON.parse(saved) : [];
    });

    useEffect(() => {
        localStorage.setItem('annadata_cart', JSON.stringify(cartItems));
    }, [cartItems]);

    const addToCart = (item: CartItem) => {
        setCartItems(prev => {
            const existing = prev.find(i => i.id === item.id);
            if (existing) {
                return prev.map(i => i.id === item.id ? { ...i, qty: i.qty + 1 } : i);
            }
            return [...prev, item];
        });
    };

    const removeFromCart = (id: string) => {
        setCartItems(prev => prev.filter(i => i.id !== id));
    };

    const updateQty = (id: string, qty: number) => {
        setCartItems(prev => prev.map(i => i.id === id ? { ...i, qty: Math.max(1, qty) } : i));
    };

    const clearCart = () => setCartItems([]);

    const buyNow = (item: CartItem) => {
        setCartItems([{ ...item, qty: Math.max(1, item.qty) }]);
    };

    const cartTotal = cartItems.reduce((sum, item) => sum + (item.price * item.qty), 0);
    const cartCount = cartItems.reduce((sum, item) => sum + item.qty, 0);

    return (
        <CartContext.Provider value={{
            cartItems,
            addToCart,
            removeFromCart,
            updateQty,
            clearCart,
            buyNow,
            cartTotal,
            cartCount
        }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const context = useContext(CartContext);
    if (context === undefined) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
